import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, ImageIcon, AudioLines, CheckCircle2, AlertTriangle, X, ArrowLeft, History, ShieldCheck, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
  ready:                { label: "Ready",              color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  identifier_conflict:  { label: "Needs Review",       color: "bg-amber-500/10  text-amber-400  border-amber-500/30" },
  ambiguous_match:      { label: "Ambiguous",          color: "bg-violet-500/10 text-violet-400 border-violet-500/30" },
  unmatched:            { label: "Unmatched Row",      color: "bg-rose-500/10   text-rose-400   border-rose-500/30" },
};

const ART_STATUS = {
  matched:    { label: "Matched",  color: "text-emerald-400" },
  missing:    { label: "Missing",  color: "text-zinc-500" },
  invalid:    { label: "Invalid",  color: "text-rose-400" },
  unmatched:  { label: "Unmatched", color: "text-zinc-500" },
};

export default function AdminBulkImport() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [targetUser, setTargetUser] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${userId}`).then((r) => setTargetUser(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "User not found"));
  }, [userId]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <button onClick={() => nav("/admin/users")}
        data-testid="bulk-back-to-users"
        className="text-sm text-zinc-400 hover:text-cyan-400 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to Users
      </button>
      <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator · Users</div>
          <h1 className="mt-1 text-4xl font-bold tracking-tight flex items-center gap-3">
            <FileSpreadsheet className="w-8 h-8 text-cyan-400" /> Bulk Song Import
          </h1>
          {targetUser && (
            <div className="mt-2 flex items-center gap-2 text-sm text-zinc-400">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Scoped to <span className="text-zinc-100 font-medium" data-testid="bulk-target-user">{targetUser.full_name}</span> · {targetUser.email}</span>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="import" className="mt-8">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="import" data-testid="bulk-tab-import">Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="bulk-tab-history"><History className="w-3.5 h-3.5 mr-1.5" /> History</TabsTrigger>
        </TabsList>
        <TabsContent value="import"><ImportFlow userId={userId} targetUser={targetUser} /></TabsContent>
        <TabsContent value="history"><HistoryTab userId={userId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ImportFlow({ userId, targetUser }) {
  const [excel, setExcel] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [wavs, setWavs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [rowResolve, setRowResolve] = useState({}); // { [rowIndex]: {replace_audio, accept_conflict, ambiguous_sub_id} }
  const excelRef = useRef(null);
  const artRef = useRef(null);
  const wavRef = useRef(null);

  const resetForm = () => {
    setExcel(null); setArtworks([]); setWavs([]); setPreview(null); setResult(null); setRowResolve({});
    if (excelRef.current) excelRef.current.value = "";
    if (artRef.current) artRef.current.value = "";
    if (wavRef.current) wavRef.current.value = "";
  };

  const runPreview = async () => {
    if (!excel) return toast.error("Choose an Excel/CSV file first");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("excel", excel);
      (artworks || []).forEach((f) => fd.append("artworks", f));
      (wavs || []).forEach((f) => fd.append("wavs", f));
      const { data } = await api.post(
        `/admin/bulk-song-import/${userId}/preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(data);
      toast.success(`Parsed ${data.counters.total_rows} rows · ${data.counters.songs_matched} songs matched`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    if (!preview) return;
    // Guard: any unresolved identifier conflict?
    const unresolvedConflicts = preview.rows.filter(r =>
      r.identifier_conflicts?.length && !(rowResolve[r.row_index]?.accept_conflict)
    );
    if (unresolvedConflicts.length) {
      toast.error(`Resolve ${unresolvedConflicts.length} identifier conflict(s) first`);
      return;
    }
    setConfirming(true);
    try {
      const body = {
        preview_id: preview.id,
        replace_audio: {},
        accept_identifier_conflicts: {},
        ambiguous_assignments: {},
      };
      preview.rows.forEach(r => {
        const rr = rowResolve[r.row_index] || {};
        if (rr.replace_audio) body.replace_audio[String(r.row_index)] = true;
        if (rr.accept_conflict) body.accept_identifier_conflicts[String(r.row_index)] = true;
        if (rr.ambiguous_sub_id) body.ambiguous_assignments[String(r.row_index)] = rr.ambiguous_sub_id;
      });
      const { data } = await api.post("/admin/bulk-song-import/confirm", body);
      setResult(data);
      toast.success(`Applied — ${data.songs_updated} song(s) updated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Confirm failed");
    } finally { setConfirming(false); }
  };

  const cancel = async () => {
    if (!preview) return;
    try { await api.post(`/admin/bulk-song-import/cancel/${preview.id}`); } catch {}
    resetForm();
    toast.message("Import cancelled — nothing was saved");
  };

  if (result) {
    return (
      <div className="mt-8 p-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          <div>
            <div className="text-lg font-semibold">Import complete</div>
            <div className="text-sm text-zinc-400">
              {result.songs_updated} song(s) updated · {result.fields_changed} field change(s) ·
              &nbsp;{result.artwork_saved} artwork saved · {result.wav_saved} WAV saved · {result.skipped_rows} skipped
            </div>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <Button onClick={resetForm} data-testid="bulk-import-new"
            className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
            Start another import
          </Button>
          <Link to="/admin/submissions" className="inline-block">
            <Button variant="outline" className="border-zinc-700 hover:bg-zinc-900">View submissions</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="mt-6 grid gap-6">
        <FilePickerCard
          testid="pick-excel"
          icon={FileSpreadsheet}
          title="Excel / CSV Metadata"
          hint="One .xlsx/.xls/.csv. Column headers like Song Name, Singer, Label, ISRC, UPC, Instagram, Spotify, Release Date are auto-detected."
          multiple={false}
          accept=".xlsx,.xls,.csv"
          files={excel ? [excel] : []}
          onChange={(fs) => setExcel(fs[0] || null)}
          inputRef={excelRef}
          required
        />
        <FilePickerCard
          testid="pick-artworks"
          icon={ImageIcon}
          title="Artwork files (JPG/JPEG only)"
          hint="Multiple. Filename must match the Excel Song Name (Dil Ke Baat.jpg → Dil Ke Baat). 1500×1500 or 3000×3000 only. PNG/WEBP/GIF are rejected."
          multiple
          accept=".jpg,.jpeg,image/jpeg"
          files={artworks}
          onChange={setArtworks}
          inputRef={artRef}
        />
        <FilePickerCard
          testid="pick-wavs"
          icon={AudioLines}
          title="Master WAV audio (optional)"
          hint="Multiple .wav files. Optional — songs without a WAV keep their existing audio. Filename must match the Excel Song Name."
          multiple
          accept=".wav,audio/wav"
          files={wavs}
          onChange={setWavs}
          inputRef={wavRef}
        />

        <div className="flex flex-wrap items-center gap-3 justify-end">
          <Button variant="outline" onClick={resetForm}
            className="border-zinc-700 hover:bg-zinc-900" data-testid="bulk-clear">
            Clear
          </Button>
          <Button
            onClick={runPreview}
            disabled={!excel || loading || !targetUser}
            data-testid="bulk-run-preview"
            className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold"
          >
            <Upload className="w-4 h-4 mr-2" /> {loading ? "Parsing…" : "Preview import"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PreviewPanel
      preview={preview}
      rowResolve={rowResolve}
      setRowResolve={setRowResolve}
      onCancel={cancel}
      onConfirm={confirm}
      confirming={confirming}
    />
  );
}

function FilePickerCard({ icon: Icon, title, hint, multiple, accept, files, onChange, inputRef, required, testid }) {
  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    onChange(multiple ? list : list.slice(0, 1));
  };
  const remove = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    onChange(next);
    if (inputRef?.current && next.length === 0) inputRef.current.value = "";
  };
  return (
    <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-cyan-400"><Icon className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="font-semibold flex items-center gap-2">{title}{required && <span className="text-rose-400">*</span>}</div>
          <div className="text-sm text-zinc-500 mt-1">{hint}</div>
          <div className="mt-3">
            <input
              type="file"
              ref={inputRef}
              multiple={multiple}
              accept={accept}
              onChange={onPick}
              data-testid={testid}
              className="text-xs text-zinc-300 file:mr-3 file:rounded-full file:border-0 file:bg-zinc-800 file:px-3 file:py-1.5 file:text-zinc-100 hover:file:bg-zinc-700"
            />
          </div>
          {files.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testid}-list`}>
              {files.map((f, i) => (
                <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-zinc-900 border border-zinc-800 text-zinc-300">
                  <Icon className="w-3 h-3" /> {f.name}
                  <button type="button" onClick={() => remove(i)} className="text-zinc-500 hover:text-rose-400"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ preview, rowResolve, setRowResolve, onCancel, onConfirm, confirming }) {
  const c = preview.counters;
  const setRow = (idx, patch) => setRowResolve((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), ...patch } }));
  return (
    <div className="mt-6 space-y-6">
      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <StatCard k="Total rows" v={c.total_rows} testid="stat-total-rows" />
        <StatCard k="Songs matched" v={c.songs_matched} tone="emerald" testid="stat-songs-matched" />
        <StatCard k="Unmatched rows" v={c.unmatched_rows} tone="rose" testid="stat-unmatched-rows" />
        <StatCard k="Identifier conflicts" v={c.identifier_conflicts} tone="amber" testid="stat-identifier-conflicts" />
        <StatCard k="Ambiguous matches" v={c.ambiguous_matches} tone="violet" testid="stat-ambiguous" />
        <StatCard k="Artwork matched / missing / invalid" v={`${c.artwork_matched} · ${c.artwork_missing} · ${c.artwork_invalid}`} testid="stat-artwork" />
        <StatCard k="WAV matched / missing / invalid" v={`${c.wav_matched} · ${c.wav_missing} · ${c.wav_invalid}`} testid="stat-wav" />
        <StatCard k="Existing audio conflict" v={c.existing_audio_conflict} tone="amber" testid="stat-audio-conflict" />
        <StatCard k="Unmatched artwork / WAV" v={`${c.unmatched_artworks} · ${c.unmatched_wavs}`} testid="stat-unmatched-files" />
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="font-semibold">Per-song preview</div>
          <div className="text-xs text-zinc-500">Nothing is saved until you press <span className="text-cyan-400">Confirm import</span>.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="p-3">Song</th>
                <th className="p-3">Match</th>
                <th className="p-3">Artwork</th>
                <th className="p-3">WAV</th>
                <th className="p-3">Field changes</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="bulk-rows-tbody">
              {preview.rows.map((r) => {
                const s = STATUS_STYLES[r.row_status] || STATUS_STYLES.unmatched;
                const rr = rowResolve[r.row_index] || {};
                const showAudioReplace = r.wav?.status === "matched" && r.wav?.existing_audio;
                return (
                  <tr key={r.row_index} data-testid={`bulk-row-${r.row_index}`} className="border-b border-zinc-900 align-top">
                    <td className="p-3">
                      <div className="font-medium">{r.song_title || <span className="text-zinc-500">—</span>}</div>
                      {r.excel?.singer_name && <div className="text-xs text-zinc-500">Singer: {r.excel.singer_name}</div>}
                      {(r.excel?.isrc || r.excel?.upc) && (
                        <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                          {r.excel?.isrc && <span>ISRC {r.excel.isrc}</span>}
                          {r.excel?.upc && <span className="ml-2">UPC {r.excel.upc}</span>}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {r.match_type === "unmatched" ? (
                        <span className="text-rose-400">No existing song</span>
                      ) : r.match_type === "isrc" || r.match_type === "upc" ? (
                        <span className="text-emerald-400">{r.match_type.toUpperCase()} match</span>
                      ) : r.match_type === "song_singer" ? (
                        <span className="text-emerald-400">Song + Singer</span>
                      ) : r.match_type === "song" ? (
                        <span className="text-cyan-400">Song title</span>
                      ) : null}
                      {r.candidate_submission_ids?.length > 1 && (
                        <div className="mt-1">
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Pick one</Label>
                          <Select value={rr.ambiguous_sub_id || ""} onValueChange={(v) => setRow(r.row_index, { ambiguous_sub_id: v })}>
                            <SelectTrigger data-testid={`bulk-row-${r.row_index}-pick-ambig`} className="bg-zinc-900 border-zinc-800 h-8 text-xs mt-1"><SelectValue placeholder="Choose song…" /></SelectTrigger>
                            <SelectContent>
                              {r.candidate_submission_ids.map(id => (
                                <SelectItem key={id} value={id}>{id.slice(0, 8)}…</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <ArtCell art={r.artwork} previewId={preview.id} />
                    </td>
                    <td className="p-3">
                      <WavCell wav={r.wav} />
                      {showAudioReplace && (
                        <label className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-300 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`bulk-row-${r.row_index}-replace-audio`}
                            checked={!!rr.replace_audio}
                            onChange={(e) => setRow(r.row_index, { replace_audio: e.target.checked })}
                          />
                          Replace existing master audio
                        </label>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {r.updates?.length ? (
                        <ul className="space-y-0.5">
                          {r.updates.slice(0, 6).map((u, i) => (
                            <li key={i} className="text-zinc-300">
                              <span className="text-zinc-500 mr-1">{u.field}:</span>
                              <span className="text-zinc-500 line-through mr-1">{String(u.old || "—")}</span>
                              <span className="text-emerald-400">{String(u.new)}</span>
                            </li>
                          ))}
                          {r.updates.length > 6 && <li className="text-zinc-500">+ {r.updates.length - 6} more</li>}
                        </ul>
                      ) : (
                        <span className="text-zinc-500">No metadata change</span>
                      )}
                      {r.identifier_conflicts?.length > 0 && (
                        <div className="mt-2 p-2 rounded-md border border-amber-500/30 bg-amber-500/5">
                          <div className="text-[11px] font-semibold text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Identifier conflict</div>
                          {r.identifier_conflicts.map((c, i) => (
                            <div key={i} className="text-[11px] text-zinc-300 mt-1">
                              <span className="uppercase text-zinc-500 mr-1">{c.field}:</span>
                              <span className="line-through text-zinc-500 mr-1">{c.old}</span>
                              → <span className="text-amber-300">{c.new}</span>
                            </div>
                          ))}
                          <label className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-300 cursor-pointer">
                            <input
                              type="checkbox"
                              data-testid={`bulk-row-${r.row_index}-accept-conflict`}
                              checked={!!rr.accept_conflict}
                              onChange={(e) => setRow(r.row_index, { accept_conflict: e.target.checked })}
                            />
                            Accept & overwrite
                          </label>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${s.color}`}>{s.label}</span>
                    </td>
                  </tr>
                );
              })}
              {preview.rows.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-zinc-500">No usable rows in this file.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(preview.unmatched_artworks?.length > 0 || preview.unmatched_wavs?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          <UnmatchedList title="Unmatched artwork" testid="unmatched-art-list" items={preview.unmatched_artworks} />
          <UnmatchedList title="Unmatched WAV files" testid="unmatched-wav-list" items={preview.unmatched_wavs} />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button variant="outline" onClick={onCancel}
          data-testid="bulk-cancel"
          className="border-zinc-700 hover:bg-zinc-900">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={confirming}
          data-testid="bulk-confirm"
          className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold"
        >
          <CheckCircle2 className="w-4 h-4 mr-2" /> {confirming ? "Applying…" : "Confirm import"}
        </Button>
      </div>
    </div>
  );
}

function ArtCell({ art, previewId }) {
  const style = ART_STATUS[art?.status] || ART_STATUS.missing;
  return (
    <div className="text-xs">
      <div className={`font-medium ${style.color}`}>{style.label}</div>
      {art?.orig_filename && <div className="text-zinc-500 truncate max-w-[180px]" title={art.orig_filename}>{art.orig_filename}</div>}
      {art?.error && <div className="text-rose-400 mt-0.5">{art.error}</div>}
      {art?.dims && <div className="text-zinc-500">{art.dims[0]}×{art.dims[1]}</div>}
      {art?.status === "matched" && (
        <img
          src={fileUrl(`/api/admin/bulk-song-import/${previewId}/artwork/${art.key}`)}
          alt="art preview" className="mt-1 h-14 w-14 object-cover rounded border border-zinc-800"
        />
      )}
    </div>
  );
}

function WavCell({ wav }) {
  const style = ART_STATUS[wav?.status] || ART_STATUS.missing;
  return (
    <div className="text-xs">
      <div className={`font-medium ${style.color}`}>{style.label}</div>
      {wav?.orig_filename && <div className="text-zinc-500 truncate max-w-[180px]" title={wav.orig_filename}>{wav.orig_filename}</div>}
      {wav?.error && <div className="text-rose-400 mt-0.5">{wav.error}</div>}
      {wav?.existing_audio && wav?.status !== "matched" && (
        <div className="text-zinc-500 mt-0.5">Existing audio kept</div>
      )}
      {wav?.existing_audio && wav?.status === "matched" && (
        <div className="text-amber-400 mt-0.5">Existing audio found</div>
      )}
    </div>
  );
}

function UnmatchedList({ title, items, testid }) {
  return (
    <div className="p-4 rounded-2xl border border-zinc-800 bg-zinc-950/60">
      <div className="font-semibold text-sm mb-2">{title} <span className="text-zinc-500 text-xs">({items.length})</span></div>
      <div data-testid={testid} className="space-y-1">
        {items.map((f) => (
          <div key={f.key} className="text-xs text-zinc-300 flex items-center justify-between gap-2 px-2 py-1 rounded bg-zinc-900 border border-zinc-800">
            <span className="truncate" title={f.orig_filename}>{f.orig_filename}</span>
            {!f.valid && <span className="text-rose-400 text-[10px]">{f.error}</span>}
            {f.valid && f.ambiguous && <span className="text-amber-400 text-[10px]">ambiguous</span>}
            {f.valid && !f.ambiguous && <span className="text-zinc-500 text-[10px]">no title match</span>}
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-zinc-500">None</div>}
      </div>
    </div>
  );
}

function StatCard({ k, v, tone, testid }) {
  const toneCls = tone === "emerald" ? "text-emerald-400" : tone === "rose" ? "text-rose-400" : tone === "amber" ? "text-amber-400" : tone === "violet" ? "text-violet-400" : "text-zinc-100";
  return (
    <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-950/60">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className={`mt-1.5 text-2xl font-semibold ${toneCls}`} data-testid={testid}>{v}</div>
    </div>
  );
}

function HistoryTab({ userId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const load = () => {
    setLoading(true);
    api.get("/admin/bulk-song-import/history", { params: { user_id: userId } })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [userId]);

  return (
    <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <th className="p-3">When</th>
            <th className="p-3">Excel</th>
            <th className="p-3 text-right">Rows</th>
            <th className="p-3 text-right">Updated</th>
            <th className="p-3 text-right">Art</th>
            <th className="p-3 text-right">WAV</th>
            <th className="p-3">Admin</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody data-testid="bulk-history-tbody">
          {loading && <tr><td colSpan="8" className="p-6 text-center text-zinc-500">Loading…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-zinc-500">No bulk imports yet for this user.</td></tr>}
          {!loading && rows.map((r) => (
            <tr key={r.id} data-testid={`bulk-history-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
              <td className="p-3 text-zinc-400 font-mono text-xs whitespace-nowrap">{new Date(r.imported_at).toLocaleString()}</td>
              <td className="p-3 truncate max-w-[200px]" title={r.excel_filename}>{r.excel_filename}</td>
              <td className="p-3 text-right font-mono">{r.total_rows}</td>
              <td className="p-3 text-right font-mono text-emerald-400">{r.songs_updated}</td>
              <td className="p-3 text-right font-mono">{r.artwork_saved}</td>
              <td className="p-3 text-right font-mono">{r.wav_saved}</td>
              <td className="p-3 text-zinc-400 text-xs">{r.admin_email}</td>
              <td className="p-3 text-right">
                <Button size="sm" variant="ghost"
                  onClick={() => api.get(`/admin/bulk-song-import/history/${r.id}`).then((res) => setSelected(res.data))}
                  data-testid={`bulk-history-view-${r.id}`}
                  className="text-zinc-400 hover:text-cyan-400">Details</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <HistoryDetail selected={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function HistoryDetail({ selected, onClose }) {
  return (
    <Dialog open={!!selected} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl" data-testid="bulk-history-detail">
        <DialogHeader>
          <DialogTitle>Import details</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {selected && <>Imported by {selected.admin_email} · file <code className="text-cyan-400">{selected.excel_filename}</code> · {selected.total_rows} rows in Excel</>}
          </DialogDescription>
        </DialogHeader>
        {selected && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {selected.row_details?.length ? (
              <div className="rounded-lg border border-zinc-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500 uppercase tracking-wider">
                      <th className="p-2">Row</th><th className="p-2">Song</th><th className="p-2">Field changes</th><th className="p-2">Art</th><th className="p-2">WAV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.row_details.map((r) => (
                      <tr key={r.row_index} className="border-t border-zinc-900">
                        <td className="p-2 text-zinc-500 font-mono">#{r.row_index}</td>
                        <td className="p-2">{r.song_title}</td>
                        <td className="p-2">
                          {r.fields.map((f, i) => (
                            <div key={i}><span className="text-zinc-500">{f.field}:</span> <span className="line-through text-zinc-500">{String(f.old || "—")}</span> → <span className="text-emerald-400">{String(f.new)}</span></div>
                          ))}
                        </td>
                        <td className="p-2">{r.artwork_replaced ? "Yes" : "—"}</td>
                        <td className="p-2">{r.audio_replaced ? "Yes" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-sm text-zinc-500">No row changes recorded.</div>}
            {selected.skipped_rows?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Skipped rows</div>
                <ul className="text-xs text-zinc-300 space-y-0.5">
                  {selected.skipped_rows.map((s, i) => <li key={i}>#{s.row_index}: <span className="text-zinc-500">{s.reason}</span></li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
